// Passe de batalha mensal: trilha free (todos) e premium (comprada na loja de donate, vale para o mês).
// Pontos vêm das missões diárias e semanais. A última recompensa premium é sempre exclusiva da temporada
// (no meio, nível 15, uma aura; no fim, nível 30, um outfit custom; nada disso se obtém de outra forma). Tudo que é item cai na Noxara Store Box.

export const BP_LEVELS = 30;
export const BP_POINTS_PER_LEVEL = 1000;
/** preço do passe premium em Noxara Coins (proposta) */
export const BP_PREMIUM_PRICE = 250;   // o mesmo preço do VIP (decisão do Arthur)

export type BpRewardKind = 'stokens' | 'gtokens' | 'fragments' | 'exercise' | 'xpboost' | 'gold' | 'coins' | 'rtoken' | 'bless' | 'aura' | 'outfit' | 'addon';
export interface BpReward { kind: BpRewardKind; amount: number; aura?: string }

export type MissionStat = 'kills' | 'elites' | 'bosses' | 'huntMinutes' | 'exercise' | 'daily' | 'quests' | 'tasks';
export interface Mission { id: string; stat: MissionStat; goal: number; points: number; text: string }

// Pontos equilibrados (decisão do Arthur, 13/10/2026): 15 dias de diárias (15 × 1.000) + 1 semana completa de semanais
// (6.000) + 10 missões de monstro (10 × 1.000) = 31.000, um pouco acima dos 30.000 do passe inteiro (folga).
export const DAILY_MISSIONS: Mission[] = [
  { id: 'd-kills', stat: 'kills', goal: 1000, points: 250, text: 'Mate 1.000 monstros' },
  { id: 'd-task', stat: 'tasks', goal: 1, points: 250, text: 'Conclua 1 tarefa de caça (qualquer uma)' },
  { id: 'd-hunt', stat: 'huntMinutes', goal: 60, points: 200, text: 'Cace por 1 hora (com o jogo aberto)' },
  { id: 'd-elite', stat: 'elites', goal: 3, points: 200, text: 'Mate 3 monstros especiais (Super, Mega ou Ultra)' },
  { id: 'd-daily', stat: 'daily', goal: 1, points: 100, text: 'Pegue a recompensa diária' },
];
export const WEEKLY_MISSIONS: Mission[] = [
  { id: 'w-kills', stat: 'kills', goal: 7000, points: 1500, text: 'Mate 7.000 monstros' },
  { id: 'w-task', stat: 'tasks', goal: 5, points: 1400, text: 'Conclua 5 tarefas de caça (quaisquer)' },
  { id: 'w-boss', stat: 'bosses', goal: 2, points: 1200, text: 'Derrote 2 bosses de hunt' },
  { id: 'w-hunt', stat: 'huntMinutes', goal: 480, points: 1200, text: 'Cace por 8 horas (com o jogo aberto)' },
  { id: 'w-ex', stat: 'exercise', goal: 500, points: 700, text: 'Use 500 cargas de itens de treino' },
];
/** Missões de monstro: uma por criatura das hunts, valem uma vez por temporada. Abates pedidos pela faixa de level da
 *  hunt da criatura: até 100 → 1.000; 100–199 → 2.000; 200–299 → 3.000... */
export const MONSTER_MISSION_POINTS = 1000;
export const monsterMissionGoal = (huntLevel: number) => 1000 * (Math.floor(Math.max(0, huntLevel - 1) / 100) + 1);

/** Auras exclusivas de cada temporada (a recompensa final premium). O mês escolhe a aura. */
export const SEASON_AURAS: Array<{ id: string; name: string; color: number }> = [
  { id: 'eclipse', name: 'Aura do Eclipse', color: 0x9b4dff },
  { id: 'carmesim', name: 'Aura Carmesim', color: 0xe02040 },
  { id: 'obsidiana', name: 'Aura de Obsidiana', color: 0x50e0ff },
  { id: 'fenda', name: 'Aura da Fenda', color: 0x40ff90 },
  { id: 'prata', name: 'Aura de Prata Lunar', color: 0xe8ecf4 },
  { id: 'ascensao', name: 'Aura da Ascensão', color: 0xffc040 },
];
export function seasonAura(season: string) {
  const [y, m] = season.split('-').map(Number);
  return SEASON_AURAS[((y! * 12 + m!) % SEASON_AURAS.length + SEASON_AURAS.length) % SEASON_AURAS.length]!;
}
/** Outfit custom exclusivo de cada temporada (a recompensa final premium). Só quem pegou naquele mês terá. */
export const SEASON_OUTFITS: Record<string, string> = { '2026-10': 'Ceifador da Abóbora' };
export function seasonOutfit(season: string): string | undefined { return SEASON_OUTFITS[season]; }
/** auras de outras fontes (marcos e loja do Coliseu) */
export const OTHER_AURAS: Array<{ id: string; name: string; color: number }> = [
  { id: 'gladiador', name: 'Aura do Gladiador', color: 0xc08040 },
  { id: 'campeao', name: 'Aura do Campeão do Coliseu', color: 0xff6020 },
  { id: 'fundador', name: 'Aura de Fundador', color: 0xffd34d },
];
export function auraById(id: string | undefined) { return SEASON_AURAS.find((a) => a.id === id) ?? OTHER_AURAS.find((a) => a.id === id); }

// sem poções (agora automáticas): Silver/Gold Tokens do imbuement e fragmentos de pedra de proteção do refine
const S = (n: number): BpReward => ({ kind: 'stokens', amount: n });
const GT = (n: number): BpReward => ({ kind: 'gtokens', amount: n });
const F = (n: number): BpReward => ({ kind: 'fragments', amount: n });
const E = (n: number): BpReward => ({ kind: 'exercise', amount: n });
const X = (n: number): BpReward => ({ kind: 'xpboost', amount: n });
const G = (n: number): BpReward => ({ kind: 'gold', amount: n });
const C = (n: number): BpReward => ({ kind: 'coins', amount: n });
/** Addons dos outfits iniciais na trilha free: 2 por mês, em rodízio — nenhum se repete até todos terem saído.
 *  O Mage fica de fora: só se consegue usando o Ferumbras' hat (decisão do Arthur, 13/10). */
export const BP_ADDON_ROTATION = ['Citizen', 'Hunter', 'Knight', 'Nobleman / Noblewoman', 'Summoner', 'Warrior', 'Barbarian', 'Druid', 'Wizard', 'Oriental', 'Monk'];
const BP_ADDON_START = 2026 * 12 + 9;   // outubro de 2026 = primeiro mês do rodízio
/** os 2 outfits com addons completos da temporada (índice 0 ou 1) */
export function seasonAddon(season: string, slot: number): string {
  const [y, m] = season.split('-').map(Number);
  const k = ((y! * 12 + (m! - 1)) - BP_ADDON_START) * 2 + slot;
  const n = BP_ADDON_ROTATION.length;
  return BP_ADDON_ROTATION[((k % n) + n) % n]!;
}
const A = (slot: number): BpReward => ({ kind: 'addon', amount: slot });
/** fichas da Roleta (1 ficha = 1 giro) */
const R = (n: number): BpReward => ({ kind: 'rtoken', amount: n });

/** Recompensas por nível (índice 0 = nível 1). null = sem recompensa nessa trilha.
 *  Trilha free: 30 Noxara Coins e 5 fichas da Roleta ao todo; premium: 7 fichas da Roleta (decisão do Arthur, 13/10). */
export const BP_FREE: Array<BpReward | null> = [
  F(1), R(1), G(30), C(5), E(100), R(1), X(30), C(5), S(1), G(50),
  R(1), E(150), C(5), X(30), F(2), R(1), G(75), C(5), E(200), X(60),
  R(1), S(2), A(0), G(100), E(250), null, X(60), A(1), F(3), C(10),
];
export const BP_PREMIUM: Array<BpReward> = [
  S(2), E(200), R(1), X(60), F(3), E(300), { kind: 'bless', amount: 1 }, G(150), X(90), R(2),
  S(3), X(60), R(1), E(500), { kind: 'aura', amount: 1 }, F(5), { kind: 'bless', amount: 1 }, X(120), E(800), E(1800),
  GT(1), R(2), E(1000), X(120), X(180), R(1), E(1800), { kind: 'bless', amount: 1 }, GT(3), { kind: 'outfit', amount: 1 },
];

export interface BpMissionState { key: string; progress: Partial<Record<MissionStat, number>>; done: string[] }
export interface BpState {
  season: string; points: number; premium: boolean; free: number[]; prem: number[];
  daily: BpMissionState; weekly: BpMissionState;
  /** missões de monstro da temporada: abates de cada criatura e as já concluídas */
  mkills?: Record<string, number>; mdone?: string[];
}

export function bpLevel(points: number) { return Math.min(BP_LEVELS, Math.floor(points / BP_POINTS_PER_LEVEL)); }

/** Chaves de dia, semana (segunda-feira) e mês no horário de Brasília. */
export function bpKeys(now = Date.now()) {
  const d = new Date(now - 3 * 3600_000);
  const day = d.toISOString().slice(0, 10);
  const monday = new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * 86400_000).toISOString().slice(0, 10);
  return { day, week: monday, season: day.slice(0, 7) };
}

/** Estado do passe normalizado para agora (temporada nova zera tudo; missões renovam no dia/semana). */
export function bpNow(s: Partial<BpState> | null | undefined, now = Date.now()): BpState {
  const k = bpKeys(now);
  const fresh = (key: string): BpMissionState => ({ key, progress: {}, done: [] });
  const st: BpState = s && s.season === k.season
    ? { season: k.season, points: s.points ?? 0, premium: !!s.premium, free: s.free ?? [], prem: s.prem ?? [], daily: s.daily ?? fresh(k.day), weekly: s.weekly ?? fresh(k.week), mkills: s.mkills ?? {}, mdone: s.mdone ?? [] }
    : { season: k.season, points: 0, premium: false, free: [], prem: [], daily: fresh(k.day), weekly: fresh(k.week), mkills: {}, mdone: [] };
  if (st.daily.key !== k.day) st.daily = fresh(k.day);
  if (st.weekly.key !== k.week) st.weekly = fresh(k.week);
  return st;
}

/** Soma progresso numa estatística; devolve as missões que acabaram de ser concluídas. */
export function bpAdd(st: BpState, stat: MissionStat, n: number): Mission[] {
  const out: Mission[] = [];
  for (const [ms, list] of [[st.daily, DAILY_MISSIONS], [st.weekly, WEEKLY_MISSIONS]] as const) {
    ms.progress[stat] = (ms.progress[stat] ?? 0) + n;
    for (const m of list) {
      if (m.stat !== stat || ms.done.includes(m.id) || (ms.progress[stat] ?? 0) < m.goal) continue;
      ms.done.push(m.id); st.points += m.points; out.push(m);
    }
  }
  return out;
}

/** Abate de uma criatura para a missão de monstro; devolve true se a missão acabou de ser concluída. */
export function bpMonsterKill(st: BpState, name: string, goal: number): boolean {
  const mk = (st.mkills ??= {}), md = (st.mdone ??= []);
  if (md.includes(name)) return false;
  mk[name] = (mk[name] ?? 0) + 1;
  if (mk[name]! < goal) return false;
  md.push(name); delete mk[name]; st.points += MONSTER_MISSION_POINTS;
  return true;
}

export function bpRewardLabel(r: BpReward, season?: string): string {
  switch (r.kind) {
    case 'stokens': return `${r.amount} Silver Token${r.amount > 1 ? 's' : ''}`;
    case 'gtokens': return `${r.amount} Gold Token${r.amount > 1 ? 's' : ''}`;
    case 'fragments': return `${r.amount} fragmento${r.amount > 1 ? 's' : ''} de proteção`;
    case 'exercise': return `Treino (${r.amount} usos)`;
    case 'xpboost': return `+50% XP ${r.amount} min`;
    case 'gold': return `${r.amount} x level em gp`;
    case 'coins': return `${r.amount} Noxara Coins`;
    case 'addon': return season ? `${seasonAddon(season, r.amount)} com os 2 addons` : 'Outfit inicial com os 2 addons';
    case 'rtoken': return `${r.amount} ficha${r.amount > 1 ? 's' : ''} da Roleta`;
    case 'bless': return 'Uma bênção';
    case 'aura': return season ? seasonAura(season).name : 'Aura exclusiva';
    case 'outfit': return (season && seasonOutfit(season)) ? `Outfit ${seasonOutfit(season)}` : 'Outfit exclusivo';
  }
}
